import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowUpRight } from 'react-icons/fi';
import PublicFilterDropdown from '../PublicFilterDropdown';
import { PillTabs, UnderlineTabs } from '../Directory';
import { MARKET_PRICE_LISTINGS, MARKET_PRICE_STATES } from '../../data/overviewData';

// ── Tabs ─────────────────────────────────────────────────────────
const PRODUCT_TABS = [
  { key: 'products',   label: 'Livestock Products' },
  { key: 'byproducts', label: 'Livestock By-Products' },
  { key: 'feed',       label: 'Feed & Fodder' },
];

// Sub-filter pills per tab
const TAB_FILTERS = {
  products:   ['Live Animals', 'Processed Livestock'],
  byproducts: ['Cattle', 'Sheep & Goats', 'Poultry', 'Camels'],
};

// Species shown for each Products sub-filter
const PRODUCT_SPECIES = {
  'Live Animals':        ['Cattle', 'Goat', 'Sheep', 'Chicken'],
  'Processed Livestock': ['Pig', 'Bushmeat'],
};

// ── Exact Figma by-products data per animal type ─────────────────
const BYPRODUCT_DATA = {
  Cattle: [
    { name: 'Hides & Skin',   unit: 'Per Kilo',   useCase: 'Leather industry (shoes, bags, upholstery)',       context: 'Leather exports are priced under NEPC indicative commodity lists. High demand in fashion and furniture sectors.', value: '₦943/kg' },
    { name: 'Bones & Hooves', unit: 'Per Kilo',   useCase: 'Bone meal for animal feed, gelatin production',   context: 'Steady demand in feed and food processing.',                                                                    value: '₦400/kg' },
    { name: 'Horns',          unit: 'Per Kilo',   useCase: 'Crafts, Buttons, Ornaments',                       context: 'Niche but profitable artisan market.',                                                                         value: '₦2,500 per horn' },
    { name: 'Blood',          unit: 'Per Litres', useCase: 'Processed into blood meal (high-protein feed supplement)', context: 'Valuable in livestock feed industry.',                                                                value: '₦500/kg' },
    { name: 'Fat (Tallow)',   unit: 'Per Crate',  useCase: 'Soap, Candles, Lubricants',                        context: 'Strong demand in household and industrial goods.',                                                             value: '₦800/kg' },
  ],
  'Sheep & Goats': [
    { name: 'Skins', unit: 'Per Kilo',   useCase: 'Morocco leather, drum skins',                          context: 'Premium export potential.',                           value: '₦5,000/tonnes' },
    { name: 'Wool',  unit: 'Per Kilo',   useCase: 'Textiles',                                              context: 'Specialized textile demand',                         value: '₦2,500/kg' },
    { name: 'Hair',  unit: 'Per Kilo',   useCase: 'Ropes, brushes',                                        context: 'Low-cost, utilitarian market.',                      value: '₦1,000/kg' },
    { name: 'Blood', unit: 'Per Litres', useCase: 'Processed into blood meal (high-protein feed supplement)', context: 'Valuable in livestock feed industry.',             value: '₦500/kg' },
  ],
  Poultry: [
    { name: 'Feathers', unit: 'Per Kilo',   useCase: 'Pillows, insulation, crafts',                        context: 'Growing demand in eco-friendly products.',           value: '₦600/kg' },
    { name: 'Manure',   unit: 'Per Kilo',   useCase: 'Organic fertilizer, biogas production',               context: 'High demand in sustainable farming',                value: '₦200/kg' },
    { name: 'Bones',    unit: 'Per Kilo',   useCase: 'Bone meal for feed',                                  context: 'Consistent demand in animal nutrition',             value: '₦400/kg' },
    { name: 'Blood',    unit: 'Per Litres', useCase: 'Processed into blood meal (high-protein feed supplement)', context: 'Valuable in livestock feed industry.',          value: '₦500/kg' },
  ],
  Camels: [
    { name: 'Hides',   unit: 'Per Kilo',   useCase: 'Leather goods',                                       context: 'Premium export potential.',                         value: '₦8,000/tonnes' },
    { name: 'Hair',    unit: 'Per Kilo',   useCase: 'Textiles, ropes',                                      context: 'Traditional uses, steady demand',                   value: '₦2,500/kg' },
    { name: 'Manure',  unit: 'Per Kilo',   useCase: 'Fertilizer',                                           context: 'Valuable in arid-region agriculture',               value: '₦150/kg' },
    { name: 'Blood',   unit: 'Per Litres', useCase: 'Processed into blood meal (high-protein feed supplement)', context: 'Valuable in livestock feed industry.',          value: '₦500/kg' },
  ],
};

// ── Feed & Fodder card data (exact from Figma 1003:70079) ────────
const FEED_CARDS = [
  { title: 'Grains',        unit: 'Per Kilo', items: [{ label: 'Maize', price: '₦7000/kg' }, { label: 'Sorghum', price: '₦6,000/kg' }, { label: 'Millet', price: '₦5,000/kg' }, { label: 'Wheat', price: '₦5,300/kg' }, { label: 'Rice', price: '₦9,800/kg' }] },
  { title: 'Legumes',       unit: 'Per Kilo', items: [{ label: 'Cowpea', price: '₦7000/kg' }, { label: 'Groundnut', price: '₦6,000/kg' }, { label: 'Soybeans', price: '₦5,000/kg' }, { label: 'Bambara Nut', price: '₦5,300/kg' }, { label: 'Pigeon pea', price: '₦9,800/kg' }] },
  { title: 'Crop residues', unit: 'Per Kilo', items: [{ label: 'Rice Bran', price: '₦7000/kg' }, { label: 'Maize Bran', price: '₦6,000/kg' }, { label: 'Cassava Peels', price: '₦5,000/kg' }, { label: 'Yam Peels', price: '₦5,300/kg' }, { label: 'Rice', price: '₦9,800/kg' }] },
  { title: 'Fodder trees',  unit: 'Per Kilo', items: [{ label: 'Moringa', price: '₦7000/kg' }, { label: 'Acacia', price: '₦6,000/kg' }, { label: 'Neem', price: '₦5,000/kg' }] },
  { title: 'Agro-industrial by-products', unit: 'Per Kilo', items: [{ label: "Brewer's Grain", price: '₦7000/kg' }, { label: 'Molasses', price: '₦6,000/kg' }, { label: 'Groundnut cake', price: '₦5,000/kg' }, { label: 'Palm Kernel Cake (PKC)', price: '₦5,300/kg' }, { label: 'Sugarcane Bagasse', price: '₦5,300/kg' }, { label: 'Cocoa pod husk', price: '₦5,300/kg' }, { label: 'Cassava Starch residue', price: '₦9,800/kg' }] },
  { title: 'Fodder Grasses',unit: 'Per Kilo', items: [{ label: 'Guinea Grass', price: '₦7000/kg' }, { label: 'Elephant Grass', price: '₦6,000/kg' }, { label: 'Gamba Grass', price: '₦5,000/kg' }, { label: 'Andropogon', price: '₦5,300/kg' }, { label: 'Panicum Maximum', price: '₦9,800/kg' }] },
  { title: 'Concentrates',  unit: 'Per Kilo', items: [{ label: 'Poultry Mash', price: '₦7000/kg' }, { label: 'Fish Meal', price: '₦6,000/kg' }, { label: 'Pelleted Feeds', price: '₦5,000/kg' }, { label: 'Mineral Licks', price: '₦9,800/kg' }] },
];

// ── Helpers ──────────────────────────────────────────────────────
function formatNaira(v) {
  return `₦${Number(v).toLocaleString('en-NG')}`;
}

function ViewMoreBtn() {
  return (
    <Link to="markets-prices" className="flex h-5 w-[60px] items-center justify-center gap-px rounded-full bg-[rgba(66,234,41,0.1)] pl-[5px] pr-[2px]">
      <span className="text-[8px] font-medium text-[#1a790c]">View More</span>
      <FiArrowUpRight className="h-3 w-3 text-[#1a790c]" />
    </Link>
  );
}

// ── Live / Processed price card ──────────────────────────────────
function SpeciesCard({ species, state }) {
  const listing = useMemo(() => {
    const pool = state && state !== 'All States'
      ? MARKET_PRICE_LISTINGS.filter((r) => r.state === state)
      : MARKET_PRICE_LISTINGS;
    return pool.find((r) => r.species === species)
        ?? MARKET_PRICE_LISTINGS.find((r) => r.species === species);
  }, [species, state]);

  if (!listing) return null;
  const cur   = listing.price;
  const prev  = Math.round(cur * 0.975);
  const change = cur - prev;
  const label  = listing.type === 'Live Animal' ? `LIVE ${species.toUpperCase()}` : species.toUpperCase();

  return (
    <div className="flex flex-1 flex-col rounded-lg border border-[rgba(187,187,187,0.2)] bg-white shadow-[0px_16px_20px_rgba(88,92,95,0.16)] gap-4 px-[10px] py-[15px]">
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center justify-between gap-1">
          <p className="font-bold text-[#8e8e8e] text-[12px]">{label}</p>
          <ViewMoreBtn />
        </div>
        <p className="font-semibold text-[#8e8e8e] text-[10px]">{listing.unit === 'Per Kg' ? 'Per Kg' : 'Per Head'}</p>
      </div>
      <div>
        <p className="font-medium text-[#606060] text-[10px]">Average Current Price (₦)</p>
        <p className="font-semibold text-[#28ba12] text-[16px]">{formatNaira(cur)}</p>
      </div>
      <div className="flex flex-col">
        {[
          { label: 'Previous Price (₦)', value: formatNaira(prev),                       color: 'text-black' },
          { label: 'Change',        value: `+${formatNaira(change)}`,               color: 'text-[#1fc16b]' },
          { label: 'Lowest Price (₦)',   value: formatNaira(Math.round(cur * 0.951)),    color: 'text-[#d00416]' },
          { label: 'Highest Price (₦)',   value: formatNaira(Math.round(cur * 1.074)),    color: 'text-[#1fc16b]' },
        ].map(({ label: l, value, color }) => (
          <div key={l} className="flex items-center justify-between border-b border-[#d2d2d2] py-[10px] text-[10px]">
            <span className="font-medium text-[#606060]">{l}</span>
            <span className={`font-medium ${color}`}>{value}</span>
          </div>
        ))}
        <div className="flex items-center justify-between py-[10px] text-[10px]">
          <span className="font-medium text-[#606060]">Data Source</span>
          <span className="font-bold text-black">{listing.source}</span>
        </div>
      </div>
    </div>
  );
}

// ── Markets Reporting card ───────────────────────────────────────
function MarketsReportingCard({ state }) {
  const sorted = useMemo(() => {
    const pool = state && state !== 'All States' ? MARKET_PRICE_LISTINGS.filter((r) => r.state === state) : MARKET_PRICE_LISTINGS;
    return [...pool].sort((a, b) => b.price - a.price);
  }, [state]);
  return (
    <div className="flex flex-1 flex-col rounded-lg border border-[rgba(187,187,187,0.2)] bg-white shadow-[0px_16px_20px_rgba(88,92,95,0.16)] gap-3 px-[10px] py-[15px]">
      <div className="flex items-center justify-between gap-1">
        <p className="font-bold text-[#8e8e8e] text-[12px]">Markets Reporting</p>
        <ViewMoreBtn />
      </div>
      <p className="font-semibold text-[#28ba12] text-[16px]">{MARKET_PRICE_LISTINGS.length}</p>
      <div>
        <p className="mb-1 font-medium text-[#606060] text-[10px]">Highest Price Market List</p>
        {sorted.slice(0, 2).map((r) => (
          <div key={r.id} className="flex items-center justify-between border-b border-[#d2d2d2] py-[10px]">
            <div className="flex flex-col">
              <span className="font-medium text-[#606060] text-[10px]">{r.market}</span>
              <span className="font-urbanist italic text-[#606060] text-[6px]">{r.state}</span>
            </div>
            <span className="font-medium text-black text-[11px]">{formatNaira(r.price)}</span>
          </div>
        ))}
      </div>
      <div>
        <p className="mb-1 font-medium text-[#606060] text-[10px]">Lowest Price Market List</p>
        {[...sorted].reverse().slice(0, 2).map((r) => (
          <div key={r.id} className="flex items-center justify-between border-b border-[#d2d2d2] py-[10px]">
            <div className="flex flex-col">
              <span className="font-medium text-[#606060] text-[10px]">{r.market}</span>
              <span className="font-urbanist italic text-[#606060] text-[6px]">{r.state}</span>
            </div>
            <span className="font-medium text-black text-[11px]">{formatNaira(r.price)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── By-Product card — exact Figma structure ───────────────────────
function ByProductCard({ card }) {
  return (
    <div className="flex flex-1 flex-col rounded-lg border border-[rgba(187,187,187,0.2)] bg-white shadow-[0px_16px_20px_rgba(88,92,95,0.16)] gap-4 px-[10px] py-[15px]">
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center justify-between gap-1">
          <p className="font-bold text-[#8e8e8e] text-[12px]">{card.name}</p>
          <ViewMoreBtn />
        </div>
        <p className="font-semibold text-[#8e8e8e] text-[10px]">{card.unit}</p>
      </div>
      <div className="flex flex-col gap-0.5">
        <p className="font-medium text-[#606060] text-[10px]">Use Case</p>
        <p className="font-semibold text-black text-[8px]">{card.useCase}</p>
      </div>
      <div className="flex flex-col">
        <div className="border-b border-[#d2d2d2] py-[5px]">
          <div className="flex flex-col gap-1 py-[4px]">
            <p className="font-medium text-[#606060] text-[10px]">Market Price Context</p>
            <p className="font-medium text-black text-[7px]">{card.context}</p>
          </div>
        </div>
        <div className="py-[10px]">
          <div className="flex flex-col gap-1">
            <p className="font-medium text-[#606060] text-[10px]">Current Commodity Value</p>
            <p className="font-medium text-black text-[8px]">{card.value}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Feed & Fodder card ────────────────────────────────────────────
function FeedCard({ card }) {
  return (
    <div className="flex flex-1 flex-col rounded-lg border border-[rgba(187,187,187,0.2)] bg-white shadow-[0px_16px_20px_rgba(88,92,95,0.16)] gap-4 px-[10px] py-[15px]">
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center justify-between gap-1">
          <p className="font-bold text-[#8e8e8e] text-[12px]">{card.title}</p>
          <ViewMoreBtn />
        </div>
        <p className="font-semibold text-[#8e8e8e] text-[10px]">{card.unit}</p>
      </div>
      <div className="flex flex-col">
        {card.items.map(({ label, price }, i) => (
          <div
            key={label}
            className={`flex items-center justify-between py-[10px] text-[10px] ${i < card.items.length - 1 ? 'border-b border-[#d2d2d2]' : ''}`}
          >
            <span className="font-medium text-[#606060]">{label}</span>
            <span className="font-medium text-black">{price}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── State filter pill — exact Figma: px-[10px] py-[4px] gap-[12px], no label
function StateFilter({ value, onChange }) {
  return (
    <PublicFilterDropdown
      value={value}
      options={MARKET_PRICE_STATES}
      onChange={onChange}
      ariaLabel="Filter by state"
      align="right"
      variant="compact"
    />
  );
}

// ── Main ──────────────────────────────────────────────────────────
export default function MarketPricesSection() {
  const [activeTab,    setActiveTab]    = useState('products');
  const [activeFilter, setActiveFilter] = useState(TAB_FILTERS.products[0]);
  const [state,        setState]        = useState(MARKET_PRICE_STATES[0]);

  const filterOptions = TAB_FILTERS[activeTab] ?? [];

  function switchTab(key) {
    setActiveTab(key);
    setActiveFilter((TAB_FILTERS[key] ?? [])[0] ?? '');
  }

  return (
    <section className="bg-[#f8f7f2] py-10 sm:py-14 lg:py-[100px]">
      <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-10 pub-xl:px-[100px]">

        <h2 className="font-quattrocento text-2xl font-bold text-[#0f3a26] sm:text-3xl lg:text-[32px]">
          LIVESTOCK MARKET PRICES
        </h2>
        <p className="mt-3 max-w-[1115px] text-sm text-[#0f3a26] sm:text-base lg:text-[18px]">
          Explore how the numbers of different animals vary across states and over time, with
          charts that show species trends by location and year
        </p>

        <div className="mt-[14px] flex flex-col gap-4">

          {/* Tabs + state filter — Figma 997:57153 (shared design, see Directory.jsx) */}
          <UnderlineTabs
            tabs={PRODUCT_TABS}
            active={activeTab}
            onChange={switchTab}
            right={<StateFilter value={state} onChange={setState} />}
          />

          {/* Sub-filter pills */}
          {filterOptions.length > 0 && (
            <PillTabs
              tabs={filterOptions.map((f) => ({ key: f, label: f }))}
              active={activeFilter}
              onChange={setActiveFilter}
            />
          )}

          {/* ── Livestock Products — card grids: 1 column on phones, 2 on
              tablets, one Figma row (flex) from 1024px ── */}
          {activeTab === 'products' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex">
              {(PRODUCT_SPECIES[activeFilter] ?? []).map((sp) => <SpeciesCard key={sp} species={sp} state={state} />)}
              <MarketsReportingCard state={state} />
            </div>
          )}

          {/* ── Livestock By-Products ── */}
          {activeTab === 'byproducts' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex">
              {(BYPRODUCT_DATA[activeFilter] ?? []).map((card) => <ByProductCard key={card.name} card={card} />)}
            </div>
          )}

          {/* ── Feed & Fodder — 2-row grid, no sub-filter ── */}
          {activeTab === 'feed' && (
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex">{FEED_CARDS.slice(0, 4).map((c) => <FeedCard key={c.title} card={c} />)}</div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex">{FEED_CARDS.slice(4).map((c) => <FeedCard key={c.title} card={c} />)}</div>
            </div>
          )}

        </div>
      </div>
    </section>
  );
}
